import {
  useMutation,
  useQueries,
  useQuery,
  UseQueryOptions,
} from '@tanstack/react-query';
import { TOASTTYPE, useToast } from '@/ui/Toast/Toast';
import { fetcher } from '@/utils/fetcher';
import { RETAIL_API_URL } from 'src/utils/envConfig';
import { useIntl } from 'react-intl';
import { ProductType } from '@/types/ProductType';
import { DerivedData, Unit, UnitSchema } from '@/types/Unit';
import { getWindowLogger } from '@/utils/remoteLogger';
import { useCurrentAuthSession } from '@/context/AuthContext';
import { LogAction, LogStatus } from '@/types/Logging';
import { fsPath } from './__path';
import { useUserInfo } from './useUserInfo';

const logger = getWindowLogger({
  file: `${fsPath}/useUnitData.ts`,
});

export enum ChannelType {
  CHANNEL_1 = '1',
  CHANNEL_2 = '2',
  CHANNEL_3 = '3',
  CHANNEL_4 = '4',
}

// exported only for unit tests
export const buildDerivedData = (raw: Unit): DerivedData => ({
  slideGroups: Object.fromEntries(
    [...raw.slideGroups]
      .sort((a, b) => a.index - b.index)
      .map((slideGroup, slideGroupIndex) => {
        const structureData = raw.structure.children.find(
          ({ slideGroupId }) => slideGroupId === slideGroup.id,
        );

        return [
          slideGroup.id,
          {
            id: slideGroup.id,
            name: slideGroup.name,
            index: slideGroupIndex,
            slides: Object.fromEntries(
              raw.slides
                .filter(({ slideGroupId }) => slideGroupId === slideGroup.id)
                .sort((a, b) => a.index - b.index)
                .map((slide, index) => {
                  const slideStructureData = structureData?.children?.find(
                    ({ slideIds }: { slideIds: string[] }) =>
                      slideIds.includes(slide.slideId),
                  );
                  if (index === 0) {
                    return [
                      slide.slideId,
                      {
                        ...slide,
                        structureData,
                      },
                    ];
                  }

                  return [
                    slide.slideId,
                    {
                      ...slide,
                      structureData: slideStructureData,
                    },
                  ];
                }),
            ),
          },
        ];
      }),
  ),
});

export const fetchUnitData = async (
  id: string,
  userId?: string,
  customerId?: string,
): Promise<Unit & { derivedData: DerivedData }> => {
  const result = await fetcher(`${RETAIL_API_URL}/units/read?id=${id}`);
  const { error } = result as any;
  if (typeof error !== 'undefined') {
    console.error('fetchUnitData error:', error);
    throw new Error(error);
  }
  const parsedResult = UnitSchema.passthrough().safeParse(result);

  if (!parsedResult.success) {
    console.error(
      'Invalid data received from API:',
      parsedResult.error.format(),
    );
    logger.error({
      action: LogAction.READ,
      status: LogStatus.FAILED,
      user: userId ?? 'unkown-user',
      customer: customerId ?? 'unkown-customer',
      unit_id: id,
      message: `${parsedResult.error.format()}`,
    });
    throw new Error(parsedResult.error.message);
  }

  const derivedData = buildDerivedData(parsedResult.data);
  return { ...parsedResult.data, derivedData };
};

export const useUnitData = (id: string | undefined) => {
  const { user } = useCurrentAuthSession();
  const { data: userInfo } = useUserInfo();

  const { data, isLoading, refetch, isSuccess, isError, isRefetching } =
    useQuery({
      queryKey: ['units', id],
      queryFn: async () => fetchUnitData(id!, user?.uid, userInfo?.customer),
      enabled: Boolean(id),
    });

  return { data, isLoading, refetch, isSuccess, isError, isRefetching };
};

export type SlideGroupsMap = NonNullable<
  ReturnType<typeof useUnitData>['data']
>['derivedData']['slideGroups'];

export type SlideGroupData = SlideGroupsMap[keyof SlideGroupsMap];

export const useMultipleUnitsData = (
  ids: readonly string[],
  options?: Omit<
    UseQueryOptions<unknown, Error, unknown>,
    'queryKey' | 'queryFn'
  >,
) => {
  const { user } = useCurrentAuthSession();
  const { data: userInfo } = useUserInfo();

  const results = useQueries({
    queries: ids.map((id) => ({
      queryKey: ['units', id],
      queryFn: () => fetchUnitData(id, user?.uid, userInfo?.customer),
      ...options,
    })),
  });

  return results;
};

type CreateUnitParams = {
  name: string;
  customer: string;
  complexityType: string;
  shelves: string[];
  subsidiaries: string[];
  scriptTemplateId?: string;
  unitTemplateId?: string;
  productType?: ProductType;
  logicEditorEnabled?: boolean;
};

export const useCreateUnit = () => {
  const { user } = useCurrentAuthSession();
  const { data: userInfo } = useUserInfo();

  const toast = useToast();
  const intl = useIntl();
  const {
    mutateAsync: createUnit,
    isPending: isLoading,
    error,
  } = useMutation({
    mutationFn: async (params: CreateUnitParams) => {
      try {
        const response = await fetcher<Unit>(`${RETAIL_API_URL}/units/create`, {
          method: 'POST',
          body: JSON.stringify({
            ...params,
            productType: params.productType ?? ProductType.Generic,
          }),
        });

        logger.info({
          action: LogAction.CREATE,
          status: LogStatus.SUCCEEDED,
          user: user?.uid ?? 'unkown-user',
          customer: userInfo?.customer ?? 'unkown-customer',
          unit_id: response.id,
        });
        return response;
      } catch (err) {
        toast({
          title: intl.formatMessage({
            defaultMessage:
              'Error! Something went wrong while creating the unit!',
            id: 'AoEemM',
          }),
          mode: TOASTTYPE.Danger,
        });

        logger.error({
          action: LogAction.CREATE,
          status: LogStatus.FAILED,
          user: user?.uid ?? 'unkown-user',
          customer: userInfo?.customer ?? 'unkown-customer',
        });
        throw err;
      }
    },
    onSuccess: () => {
      toast({
        subtitle: intl.formatMessage({
          defaultMessage: 'The Unit has been created.',
          id: 'W0j3g9',
        }),
        mode: TOASTTYPE.Success,
      });
    },
  });

  return { createUnit, isLoading, error };
};
